import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ProductCard } from "@/components/ProductCard";
import { WaitlistForm } from "@/components/WaitlistForm";
import { getProducts } from "@/lib/api";
import { ArrowRight, Heart, Truck, Leaf } from "lucide-react";
import { Paw } from "@/components/Paw";

const HERO_IMG = "/hound-hoodie-cutout-v11.png";
const MATCH_HUMAN = "/george-bandana-cream.png";
const MATCH_DOG = "https://static.prod-images.emergentagent.com/jobs/59429369-2cd1-410e-bb67-3d857912cf9b/images/da8e539ea60d7e7182a47bfba84bd9a2353942c0b7fac522acc433aeaf7754c6.jpeg";
const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/59429369-2cd1-410e-bb67-3d857912cf9b/images/b13da4a1bd15b7b08c7ac05a95dc4500010cb09b7ff6593705d12dcf9008009d.jpeg";
const TEE_IMG = "https://customer-assets.emergentagent.com/job_george-henry-co/artifacts/jsjj9k24_Hound%20Ringer%20Tee.jpg";

const RIBBON = [
  "Made for you & your hound",
  "Nap-tested & treat-approved",
  "Designed in Australia",
  "Oversized & effortless",
  "Cosy essentials for dog lovers",
];

const REVIEWS = [
  { quote: "Ten paws up. I only have four, but I'd give more if I could.", author: "Biscuit", role: "Verified Good Boy" },
  { quote: "So cosy I refused to leave the couch all weekend. No regrets, only naps.", author: "Poppy", role: "Professional Napper" },
  { quote: "Chewed the tag, kept the jumper. It didn't stand a chance — and neither did I.", author: "Duke", role: "Chief Zoomies Officer" },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  const newArrivals = products.filter((p) => p.new_arrival && !p.coming_soon).slice(0, 4);
  const comingSoon = products.filter((p) => p.coming_soon);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        {/* colour layers */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute -top-28 -left-24 w-[28rem] h-[28rem] rounded-full bg-rosegold/20 blur-3xl" />
          <div className="absolute top-20 right-1/4 w-80 h-80 rounded-full bg-[#F3C6D1]/50 blur-3xl" />
          <div className="absolute top-32 -right-10 w-96 h-96 rounded-full bg-maroon/10 blur-3xl" />
          <div className="absolute -bottom-10 left-1/4 w-80 h-80 rounded-full bg-white/60 blur-3xl" />
        </div>
        {/* faded wordmark watermark */}
        <img
          src="/hound-logo.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute left-0 top-1/2 -translate-y-1/2 w-full opacity-[0.04] z-0"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-5">The Founders Collection</p>
            <h1 className="font-elegant font-semibold text-5xl sm:text-6xl lg:text-7xl text-maroon leading-[1.02] tracking-tight">
              Made for <span className="italic font-medium">you</span><br />and your <span className="italic font-medium">Hound</span>
            </h1>
            <p className="mt-6 text-lg text-maroon/85 font-medium max-w-md leading-relaxed">
              Cosy essentials for dog lovers, inspired by George &amp; Henry.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="inline-flex items-center justify-center gap-2 h-13 px-8 py-4 rounded-full bg-maroon text-cream hover:bg-maroon-dark transition-transform hover:-translate-y-0.5"
              >
                Shop the Founders Collection <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <Link
                to="/pre-orders"
                data-testid="hero-preorder-cta"
                className="inline-flex items-center justify-center h-13 px-8 py-4 rounded-full border border-maroon text-maroon hover:bg-maroon/5 transition-colors"
              >
                Join the pre-order list
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative max-w-md lg:max-w-xl mx-auto lg:mr-0 lg:ml-auto"
          >
            <div className="relative">
              <img src={HERO_IMG} alt="The HOUND hoodie in deep maroon" className="block w-full object-contain drop-shadow-2xl" />
            </div>
            <p className="mt-5 text-center italic text-rosegold font-medium max-w-xs mx-auto">
              Our signature maroon hoodie, cosy, oversized and made to be lived in.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-rosegold/15 bg-subtle/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          {[
            { icon: Heart, t: "Designed with love", s: "For humans & hounds alike" },
            { icon: Leaf, t: "Soft, quality fabrics", s: "Cosy essentials that last" },
            { icon: Truck, t: "Pre-order favourites", s: "Reserve before they sell out" },
          ].map((f) => (
            <div key={f.t} className="flex items-center justify-center gap-3">
              <f.icon className="w-5 h-5 text-rosegold" strokeWidth={1.5} />
              <div className="text-left">
                <p className="text-sm text-maroon font-semibold">{f.t}</p>
                <p className="text-xs text-inkbrown/70">{f.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Running banner — straight maroon marquee */}
      <section className="relative h-24 sm:h-28 bg-cream overflow-hidden" data-testid="paw-ribbon">
        <div className="absolute w-full left-0 top-1/2 -translate-y-1/2 bg-maroon py-4 shadow-lg">
          <div className="flex w-max animate-marquee">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center shrink-0" aria-hidden={dup === 1}>
                {RIBBON.map((t, i) => (
                  <span key={`${dup}-${i}`} className="flex items-center shrink-0">
                    <img src="/hound-logo-cream.png" alt="Hound Oz" className="h-6 sm:h-7 w-auto mx-8 select-none" />
                    <Paw className="w-4 h-4 text-rosegold -rotate-12" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">Fresh in</p>
            <h2 className="font-display text-3xl sm:text-4xl text-maroon">New Arrivals</h2>
          </div>
          <Link to="/shop" data-testid="view-all-shop" className="text-sm text-inkbrown/70 hover:text-maroon transition-colors flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
          {newArrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Product spotlight */}
      <section className="relative overflow-hidden bg-cream border-y border-rosegold/15">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 items-center">
          <div className="px-5 sm:px-8 py-20 relative z-10 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">The first drop</p>
            <h2 className="font-display text-3xl sm:text-4xl text-maroon">The HOUND Tee</h2>
            <p className="mt-4 text-maroon/80 font-medium max-w-md leading-relaxed">
              Our signature bubble logo on a soft, oversized tee with a deep‑maroon ringer trim.
              Designed in Australia, made to be lived in.
            </p>
            <Link
              to="/shop"
              data-testid="spotlight-cta"
              className="mt-7 inline-flex items-center gap-2 h-12 px-7 py-3 rounded-full bg-maroon text-cream hover:bg-maroon-dark transition-transform hover:-translate-y-0.5"
            >
              Shop the collection <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
          <div className="relative h-80 sm:h-96 lg:h-[30rem] order-1 lg:order-2">
            <img
              src={TEE_IMG}
              alt="The HOUND ringer tee"
              className="absolute inset-0 w-full h-full object-cover opacity-95 lg:[mask-image:linear-gradient(to_right,transparent,black_40%)]"
            />
          </div>
        </div>
      </section>

      {/* Everyday Hound Essentials */}
      <section className="bg-subtle py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-6 items-center">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-3xl overflow-hidden aspect-[3/4] mt-8 bg-white">
              <img src={MATCH_HUMAN} alt="George's Signature Bandana" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[3/4] bg-white">
              <img src={MATCH_DOG} alt="Henry's Water Bottle & Biscuit Feeder" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:pl-10">
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">For you &amp; your hound</p>
            <h2 className="font-display text-3xl sm:text-4xl text-maroon leading-tight">Everyday Hound Essentials</h2>
            <p className="mt-5 text-maroon/80 font-medium leading-relaxed max-w-md">
              The little extras that make life with your dog easier and a lot more stylish —
              from George's signature bandana to Henry's water bottle &amp; biscuit feeder and
              Mum's puffer tote for carrying it all.
            </p>
            <Link
              to="/shop?category=coming-soon"
              data-testid="shop-accessories-cta"
              className="mt-7 inline-flex items-center gap-2 h-12 px-7 py-3 rounded-full bg-maroon text-cream hover:bg-maroon-dark transition-transform hover:-translate-y-0.5"
            >
              See What's Coming <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      {comingSoon.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">On its way</p>
            <h2 className="font-display text-3xl sm:text-4xl text-maroon">Coming Soon</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 max-w-4xl mx-auto">
            {comingSoon.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* About the brand */}
      <section className="bg-maroon text-cream py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-[2.5rem] overflow-hidden aspect-[5/4] order-2 lg:order-1">
            <img src={ABOUT_IMG} alt="George and Henry, the founding hounds" className="w-full h-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-4">Our story</p>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">A label built on cosy afternoons &amp; long ears.</h2>
            <p className="mt-6 text-cream/80 leading-relaxed max-w-lg">
              Hound Oz began with two basset hounds, George &amp; Henry, and a simple idea: that
              the things we wrap ourselves in should be soft, beautiful and made to be shared with
              the ones we love most, four legs and all.
            </p>
            <Link
              to="/about"
              data-testid="about-cta"
              className="mt-8 inline-flex items-center gap-2 h-12 px-7 py-3 rounded-full bg-cream text-maroon hover:bg-white transition-transform hover:-translate-y-0.5"
            >
              Read our story <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* Woofs of Approval */}
      <section className="bg-subtle py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">Tail-wagging reviews</p>
            <h2 className="font-display text-3xl sm:text-4xl text-maroon">Woofs of Approval</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-3xl bg-white p-7 border border-rosegold/15"
                data-testid={`review-${r.author.toLowerCase()}`}
              >
                <div className="flex gap-1.5 mb-4">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Paw key={s} className="w-4 h-4 text-maroon -rotate-12" />
                  ))}
                </div>
                <p className="text-inkbrown font-medium leading-relaxed italic">"{r.quote}"</p>
                <p className="mt-5 font-bubble font-bold text-maroon text-lg">{r.author}</p>
                <p className="text-xs text-inkbrown/55">{r.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section className="bg-[#F5DCE2] py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">Join the pack</p>
          <h2 className="font-display text-3xl sm:text-4xl text-maroon">Be first to the good stuff</h2>
          <p className="mt-4 text-maroon/80 font-medium max-w-lg mx-auto">
            Sign up for early access to new drops, pre-order windows and cosy stories from George &amp; Henry.
          </p>
          <div className="mt-8 flex justify-center">
            <WaitlistForm source="homepage-waitlist" />
          </div>
        </div>
      </section>
    </div>
  );
}
